import Head from "next/head";
import { useRouter } from "next/router";
import { isPreviewEnv } from "../data/js/server-constants";

// Get base URL based on environment
function getSiteUrl(): string {
   if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production') {
      return 'https://sellwild.com';
   }
   if (isPreviewEnv()) {
      return 'https://new.sellwild.com';
   }
   // Local dev or unknown - use staging
   return process.env.NEXT_PUBLIC_STG_DOMAIN?.replace(/\/$/, '') || 'https://new.sellwild.com';
}

const DEFAULT_OG_IMAGE_PATH = '/images/logo.png';
const DEFAULT_OG_IMAGE_ALT = 'Sellwild Marketplace';

function SellwildHead(props) {
   const router = useRouter();
   const {
      description,
      canonicalURL,
      title,
      metaImage,
      metaImageAlt,
      children,
      favicon = "/images/favicons/favicon.ico"
   } = props;

   // Build canonical URL from current path if not provided
   const siteUrl = getSiteUrl();
   const currentPath = router.asPath.split('?')[0]; // Remove query params
   const resolvedCanonical = canonicalURL || `${siteUrl}${currentPath === '/' ? '' : currentPath}`;

   // Use defaults for og:image if not provided
   const resolvedImage = metaImage || `${siteUrl}${DEFAULT_OG_IMAGE_PATH}`;
   const resolvedImageAlt = metaImageAlt || DEFAULT_OG_IMAGE_ALT;

   // Only noindex non-production environments
   const isProduction = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';
   const noIndex = isProduction ? null : <meta name="robots" content="noindex" />;

   return (
      <Head>
         <title className="meta-head-title">{title}</title>
         {noIndex}
         <meta
            name="twitter:title"
            className="social-meta-title"
            content={title}
         />
         <meta
            property="og:title"
            className="social-meta-title"
            content={title}
         />
         <meta charSet="UTF-8" />
         <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, viewport-fit=cover"
         />

         <meta name="application-name" content={title} />
         <meta
            name="description"
            className="meta-description"
            content={description}
         />
         <meta
            property="og:description"
            className="meta-description"
            content={description}
         />
         <meta
            name="twitter:description"
            className="meta-description"
            content={description}
         />
         <link rel="canonical" className="meta-canonical" href={resolvedCanonical} />
         <meta
            name="twitter:url"
            className="social-canonical"
            content={resolvedCanonical}
         />
         <meta
            property="og:url"
            className="social-canonical"
            content={resolvedCanonical}
         />
         <meta name="msapplication-TileColor" content="#FFFFFF" />
         <meta name="msapplication-TileImage" content="mstile-144x144.png" />
         <meta property="og:image" className="meta-image" content={resolvedImage} />
         <meta
            property="og:image:alt"
            className="meta-image-alt"
            content={resolvedImageAlt}
         />
         <meta property="og:locale" content="en_US" />
         <meta property="og:type" content="website" />
         <meta
            name="twitter:image"
            className="meta-image"
            content={resolvedImage}
         />
         <meta
            name="twitter:image:alt"
            className="meta-image-alt"
            content={resolvedImageAlt}
         />
         <meta name="twitter:card" content="summary_large_image" />
         <link rel="icon" href={favicon} />

         <meta name="theme-color" content="#333" />
         {children}
      </Head>
   );
}

export default SellwildHead;
